/**
 * Extracts initials from a name string
 * Takes the first letter of each word and returns them uppercased
 *
 * @param text - The text to extract initials from
 * @param maxInitials - Maximum number of initials to return (default: 2)
 * @returns Uppercased initials
 *
 * @example
 * getInitials('John Doe') // 'JD'
 * getInitials('Mary Jane Watson') // 'MJ' (only first 2)
 * getInitials('Mary Jane Watson', 3) // 'MJW'
 * getInitials('John') // 'J'
 * getInitials('') // ''
 * getInitials(null) // ''
 * getInitials(undefined) // ''
 */
export function getInitials(
  text?: string | null,
  maxInitials: number = 2,
): string {
  if (!text) return '';

  const words = text.trim().split(/\s+/);
  const initials = words
    .slice(0, maxInitials)
    .map((word) => [...word][0])
    .filter(Boolean)
    .join('')
    .toUpperCase();

  return initials;
}

/**
 * Formats a person's name as "FirstName L." (first name with last initial)
 * Capitalizes the first letter of the first name and includes last initial with period
 *
 * @param firstName - The person's first name
 * @param lastName - The person's last name (optional)
 * @returns Formatted name
 *
 * @example
 * getShortName('john', 'doe') // 'John D.'
 * getShortName('John', 'Doe') // 'John D.'
 * getShortName('john', '') // 'John'
 * getShortName('john', null) // 'John'
 * getShortName('john', undefined) // 'John'
 * getShortName('', 'doe') // ''
 * getShortName(null, 'doe') // ''
 * getShortName('  john  ', '  doe  ') // 'John D.'
 */
export function getShortName(
  firstName?: string | null,
  lastName?: string | null,
): string {
  if (!firstName || !firstName.trim()) return '';

  const cleanFirstName = firstName.trim();
  const firstNameChars = [...cleanFirstName];
  const capitalizedFirstName =
    firstNameChars[0].toUpperCase() + firstNameChars.slice(1).join('');

  if (!lastName || !lastName.trim()) {
    return capitalizedFirstName;
  }

  const cleanLastName = lastName.trim();
  const lastInitial = [...cleanLastName][0].toUpperCase();

  return `${capitalizedFirstName} ${lastInitial}.`;
}
